import "@/App.css";
import "@/index.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import ModeSelect from "@/pages/ModeSelect";
import Assessment from "@/pages/Assessment";
import Summary from "@/pages/Summary";
import ActionPlan from "@/pages/ActionPlan";
import Export from "@/pages/Export";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SharedView from "@/pages/SharedView";
import Account from "@/pages/Account";
import AdminEfforts from "@/pages/AdminEfforts";
import AiStatus from "@/pages/AiStatus";
import MarketingConsole from "@/pages/MarketingConsole";
import AdminConsole from "@/pages/AdminConsole";
import RegenShare from "@/pages/RegenShare";
import Feedback from "@/pages/Feedback";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import Advisors from "@/pages/Advisors";
import LearnMore from "@/pages/LearnMore";
import BlogArticle from "@/pages/BlogArticle";
import Testimonials from "@/pages/Testimonials";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import ConsentBanner, { getConsent } from "@/components/ConsentBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import SeoHead from "@/components/SeoHead";
import { initMetaPixel, isMetaPixelInitialized, trackEvent, setPixelId } from "@/lib/metaPixel";

function AppRouter() {
  const location = useLocation();
  const lastUrl = useRef(null);

  // Meta Pixel PageView on SPA route changes (only after marketing consent).
  useEffect(() => {
    if (getConsent()?.analytics !== true) return;
    if (!isMetaPixelInitialized()) {
      // initMetaPixel() emits the first PageView itself.
      initMetaPixel();
      lastUrl.current = `${location.pathname}${location.search}`;
      return;
    }
    const url = `${location.pathname}${location.search}`;
    if (lastUrl.current === url) return;
    lastUrl.current = url;
    trackEvent("PageView");
  }, [location.pathname, location.search]);

  // Synchronously detect session_id in hash before any Routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/mode" element={<ModeSelect />} />
      <Route path="/assessment/:id" element={<Assessment />} />
      <Route path="/summary/:id" element={<Summary />} />
      <Route path="/plan/:id" element={<ActionPlan />} />
      <Route path="/export/:id" element={<Export />} />
      <Route path="/shared/:token" element={<SharedView />} />
      <Route path="/regen-share/:regen_token" element={<RegenShare />} />
      <Route path="/feedback/:token" element={<Feedback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin/efforts" element={<AdminEfforts />} />
      <Route path="/admin/ai-status" element={<AiStatus />} />
      <Route path="/admin/marketing" element={<MarketingConsole />} />
      <Route path="/admin" element={<AdminConsole />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/advisors" element={<Advisors />} />
      <Route path="/faq" element={<Navigate to="/learn-more" replace />} />
      <Route path="/learn-more" element={<LearnMore />} />
      <Route path="/learn-more/:slug" element={<BlogArticle />} />
      <Route path="/testimonials" element={<Testimonials />} />
    </Routes>
  );
}

function App() {
  // Load the admin-configured Meta Pixel ID (public, non-sensitive) so it can
  // be changed from the Meta connect card without a redeploy. No tracking here.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    fetch(`${base}/api/marketing/public-config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.meta_pixel_id) setPixelId(d.meta_pixel_id); })
      .catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <SeoHead />
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          <ConsentBanner />
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
